import { useEffect, useRef } from 'react';
import Map from 'ol/Map';
import { transform } from 'ol/proj';
import { useOpenLayersContext } from '@ais-ui/react-openlayers';
import { isValidCoordinate } from '@ais-ui/utils';
import { Port } from '../AISMapContext';
import { useMapContextMenu, PortFeature, ShapeFeature, CoordinateFeature } from './index';
import { GeoJson } from '@ais-ui/api-spec/shapes';

interface ListenForContextMenuProps {
  map: Map;
}

export const ListenForContextMenu = ({ map }: ListenForContextMenuProps) => {
  const { openContextMenu } = useMapContextMenu();
  const { displayProjection, dataProjection } = useOpenLayersContext();
  
  // Track if a layer-contextmenu event was handled to prevent duplicate handling
  const layerContextMenuHandledRef = useRef(false);

  useEffect(() => {
    const handleLayerContextMenu = (event: Event) => {
      // Mark that we're handling a layer context menu
      layerContextMenuHandledRef.current = true;
      
      const customEvent = event as CustomEvent;
      const { properties, geometry, position } = customEvent.detail;

      if (properties?.type === 'port') {
        const port: Port = {
          id: properties.id,
          name: properties.name,
          country: properties.country,
          locode: properties.locode,
          geometry: properties.geometry,
        };

        const portFeature: PortFeature = {
          type: 'port',
          data: port,
        };

        openContextMenu(position, portFeature);
      } else if (properties?.type === 'shape') {
        // Reconstruct GeoJson from feature properties and geometry
        // The geometry is now provided separately from the feature
        const geoJson: GeoJson = {
          type: 'Feature',
          properties: {
            id: properties.id,
            name: properties.name,
            description: properties.description,
            owner: properties.owner,
            radius: properties.radius,
            color: properties.color,
            transparency: properties.transparency,
            parentId: properties.parentId,
            acl: properties.acl,
            area: properties.area,
          },
          geometry: geometry,
        };

        const shapeFeature: ShapeFeature = {
          type: 'shape',
          data: geoJson,
        };

        openContextMenu(position, shapeFeature);
      }
    };

    const handleMapContextMenu = (event: MouseEvent) => {
      // Reset the flag at the start of each context menu event
      // Use a small timeout to allow layer-contextmenu to fire first
      setTimeout(() => {
        if (layerContextMenuHandledRef.current) {
          // Reset the flag for next time
          layerContextMenuHandledRef.current = false;
          return;
        }

        // Prevent browser context menu
        event.preventDefault();

        // Get map coordinate from event
        const coordinate = map.getEventCoordinate(event);
        if (!coordinate) return;

        // Transform to EPSG:4326 (lon/lat)
        const [longitude, latitude] = transform(
          coordinate,
          displayProjection,
          dataProjection,
        );

        // Validate coordinates
        if (!isValidCoordinate(longitude, latitude)) return;

        const coordinateFeature: CoordinateFeature = {
          type: 'coordinate',
          data: {
            longitude,
            latitude,
          },
        };

        openContextMenu({ x: event.clientX, y: event.clientY }, coordinateFeature);
      }, 0);
    };

    const preventDefaultContextMenu = (event: MouseEvent) => {
      // Always prevent default to avoid browser menu showing
      event.preventDefault();
    };

    const mapElement = map.getTargetElement();

    if (mapElement) {
      mapElement.addEventListener('layer-contextmenu', handleLayerContextMenu);
      mapElement.addEventListener('contextmenu', preventDefaultContextMenu);
      mapElement.addEventListener('contextmenu', handleMapContextMenu);
    }

    return () => {
      if (mapElement) {
        mapElement.removeEventListener('layer-contextmenu', handleLayerContextMenu);
        mapElement.removeEventListener('contextmenu', preventDefaultContextMenu);
        mapElement.removeEventListener('contextmenu', handleMapContextMenu);
      }
    };
  }, [map, openContextMenu, displayProjection, dataProjection]);

  return null;
};
